import React, { FC } from 'react';
import {
  Filters,
  PhysicalLocationFilter,
  SelectedLocation,
  TimeZone,
  VirtualLocationFilter,
} from '../../utils/types';
import { PhysicalVirtualLocationEnum } from '../../utils/enums';
import TZComboBox from '../shared/comboBoxes/TZComboBox';
import { Tooltip } from '@material-tailwind/react';
import LocationsComboBox from '../shared/comboBoxes/LocationsComboBox';
import MeetingLengthsComboBox from '../shared/comboBoxes/MeetingLengthsComboBox';
import { MdPhonelink, MdLocationPin } from 'react-icons/md';
import generateAddress from '../../utils/helpers/generateAddress';

type Props = {
  timeZones: TimeZone[];
  selectedTimeZone: TimeZone;
  setSelectedTimeZone: React.Dispatch<React.SetStateAction<TimeZone>>;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  selectedLocation: SelectedLocation;
  setSelectedLocation: React.Dispatch<React.SetStateAction<SelectedLocation>>;
  showFlexCalFiltersMobile: boolean;
};

const CalendarSideBar: FC<Props> = ({
  timeZones,
  selectedTimeZone,
  setSelectedTimeZone,
  filters,
  setFilters,
  selectedLocation,
  setSelectedLocation,
  showFlexCalFiltersMobile,
}) => {
  //handle sidebar change events
  const handleDurationChange = (position: number) => {
    //Filter by specific duration/meeting length
    setFilters({
      ...filters,
      durations: filters.durations.map((element, index) =>
        index === position
          ? { duration: element.duration, isChecked: !element.isChecked }
          : { duration: element.duration, isChecked: element.isChecked }
      ),
    });
  };

  const handleLocationChange = (
    location: PhysicalLocationFilter | VirtualLocationFilter | undefined,
    locationType: PhysicalVirtualLocationEnum | undefined
  ) => {
    //Filter events by specific physical location (if events has at least one checked location it will remain present)
    if (location === undefined) {
      setSelectedLocation({
        id: 0,
        name: 'Please choose',
        locationType: undefined,
      });
    } else if (locationType === PhysicalVirtualLocationEnum.physical) {
      const physicalLocation = location as PhysicalLocationFilter;
      setSelectedLocation({
        id: physicalLocation.placeId,
        name: physicalLocation.name,
        locationType: locationType,
      });
    } else {
      const virtualLocation = location as VirtualLocationFilter;
      setSelectedLocation({
        id: virtualLocation.virtualLocationId,
        name: virtualLocation.name,
        locationType: locationType,
      });
    }
  };

  return (
    <div className="flex flex-col w-full gap-1 p-0 lg:gap-5 lg:py-10 lg:pl-5 lg:min-w-full lg:flex-col">
      <div className="flex flex-col w-full p-0 lg:py-2 lg:w-full">
        <h4 className="hidden lg:block">Time Zone</h4>
        <div
          className={`px-1 ${
            showFlexCalFiltersMobile ? 'hidden' : ''
          } lg:block`}
        >
          <TZComboBox
            options={timeZones}
            selectedOption={selectedTimeZone}
            setSelectedOption={setSelectedTimeZone}
          />
        </div>
      </div>
      <div
        className={`flex-col hidden w-full lg:flex ${
          showFlexCalFiltersMobile ? 'hidden' : ''
        }
        `}
      >
        <h4>Meeting Length</h4>
        {filters.durations
          .sort((a, b) => {
            return a.duration - b.duration;
          })
          .map((duration, index) => (
            <div key={index} className="flex flex-row gap-2 pl-2">
              <input
                type="checkbox"
                className="cursor-pointer"
                style={{ accentColor: '#86E3A8' }}
                id={duration.duration.toString()}
                name={duration.duration.toString()}
                checked={duration.isChecked}
                onChange={() => handleDurationChange(index)}
                value={duration.duration}
              />
              <label htmlFor={duration.duration.toString()}>
                {duration.duration} min
              </label>
            </div>
          ))}
      </div>
      <div
        className={`block w-full px-1 lg:hidden ${
          showFlexCalFiltersMobile ? 'hidden' : ''
        }`}
      >
        <MeetingLengthsComboBox
          meetingLengths={filters.durations}
          handleMeetingLengthsChange={handleDurationChange}
        />
      </div>
      <div className="flex-col hidden w-full lg:flex">
        <h4 className="text-lg">Locations</h4>
        <div className="flex flex-row gap-1 pl-2">
          <input
            id="all"
            name="locations"
            type="radio"
            value="all"
            className="bg-gray-100 border-gray-300 cursor-pointer accent-confirmed-primary"
            checked={selectedLocation.id === 0}
            onChange={() => handleLocationChange(undefined, undefined)}
          />
          <label htmlFor="all">All</label>
        </div>
        <div className="flex flex-row items-center">
          <MdLocationPin
            className={` ${
              filters.physicalLocations.length === 0 ? 'hidden' : 'block'
            } `}
          />
          <h5
            className={`pl-1 ${
              filters.physicalLocations.length === 0 ? 'hidden' : 'block'
            } `}
          >
            Physical
          </h5>
        </div>
        {filters.physicalLocations.map((physicalLocation, index) => (
          <div key={index} className="flex flex-row gap-2 pl-2">
            <input
              id={physicalLocation.name}
              name="locations"
              type="radio"
              value={physicalLocation.placeId}
              className="bg-gray-100 border-gray-300 cursor-pointer accent-confirmed-primary"
              checked={
                selectedLocation.locationType ===
                  PhysicalVirtualLocationEnum.physical &&
                selectedLocation.id === physicalLocation.placeId
              }
              onChange={() =>
                handleLocationChange(
                  physicalLocation,
                  PhysicalVirtualLocationEnum.physical
                )
              }
            />
            <Tooltip
              content={generateAddress(physicalLocation)}
              className="max-w-xs"
            >
              <label className="truncate" htmlFor={physicalLocation.name}>
                {physicalLocation.name}
              </label>
            </Tooltip>
          </div>
        ))}
        <div className="flex flex-row items-center">
          <MdPhonelink
            className={` ${
              filters.virtualLocations.length === 0 ? 'hidden' : 'block'
            } `}
          />
          <h5
            className={`pl-1 ${
              filters.virtualLocations.length === 0 ? 'hidden' : 'block'
            }  `}
          >
            Virtual
          </h5>
        </div>
        {filters.virtualLocations.map((virtualLocation, index) => (
          <div key={index} className="flex flex-row gap-2 pl-2">
            <input
              id={virtualLocation.name}
              name="locations"
              type="radio"
              value={virtualLocation.virtualLocationId}
              className="bg-gray-100 border-gray-300 cursor-pointer accent-confirmed-primary"
              checked={
                selectedLocation.locationType ===
                  PhysicalVirtualLocationEnum.virtual &&
                selectedLocation.id === virtualLocation.virtualLocationId
              }
              onChange={() =>
                handleLocationChange(
                  virtualLocation,
                  PhysicalVirtualLocationEnum.virtual
                )
              }
            />
            <Tooltip content={virtualLocation.name}>
              <label className="truncate" htmlFor={virtualLocation.name}>
                {virtualLocation.name}
              </label>
            </Tooltip>
          </div>
        ))}
      </div>
      <div
        className={`block w-full px-1 pb-4 lg:hidden ${
          showFlexCalFiltersMobile ? 'hidden' : ''
        }`}
      >
        <LocationsComboBox
          physicalLocations={filters.physicalLocations}
          virtualLocations={filters.virtualLocations}
          selectedLocation={selectedLocation}
          setSelectedLocation={handleLocationChange}
        />
      </div>
      <p className="hidden text-sm lg:block">
        Powered by{' '}
        <a
          href="https://confirmedapp.com"
          target="_blank"
          className="text-confirmed-secondary"
        >
          <img
            src="/favicon.ico"
            alt="Confirmed Logo"
            width={20}
            height={20}
            className="inline"
          />
          ConfirmedApp.com
        </a>
      </p>
    </div>
  );
};

export default CalendarSideBar;
